import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import {
  APP_VERSION_PLATFORMS,
  AppVersion,
  AppVersionCreatePayload,
  AppVersionPlatform,
  AppVersionUpdatePayload,
} from "@/domain/app-versions.interface";
import appVersionValidationSchema, {
  AppVersionFormData,
} from "./validation.schema";

type AppVersionModalProps = {
  open: boolean;
  onClose: () => void;
  appVersion?: AppVersion;
  onSave: (
    data: AppVersionCreatePayload | AppVersionUpdatePayload,
    isEdit: boolean
  ) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: AppVersionFormData = {
  platform: "ios",
  version: "",
  apiVersion: "",
  functionalVersion: "",
  releaseNotes: "",
  storeUrl: "",
};

const PLATFORM_LABELS: Record<AppVersionPlatform, string> = {
  ios: "iOS",
  android: "Android",
  backoffice: "Backoffice",
};

const AppVersionModal: FC<AppVersionModalProps> = ({
  open,
  onClose,
  appVersion,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const isEdit = !!appVersion;

  const platformOptions = useMemo(
    () =>
      APP_VERSION_PLATFORMS.map((p) => ({
        key: p,
        value: PLATFORM_LABELS[p] || p,
      })),
    []
  );

  const methods = useForm<AppVersionFormData>({
    resolver: yupResolver(appVersionValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (appVersion) {
      reset({
        platform: (appVersion.platform as AppVersionPlatform) || "ios",
        version: appVersion.version || "",
        apiVersion: appVersion.apiVersion || "",
        functionalVersion: appVersion.functionalVersion || "",
        releaseNotes: appVersion.releaseNotes || "",
        storeUrl: appVersion.storeUrl || "",
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [appVersion, reset]);

  const onSubmit = async (data: AppVersionFormData) => {
    const base = {
      platform: data.platform as AppVersionPlatform,
      version: data.version.trim(),
      apiVersion: data.apiVersion.trim(),
      functionalVersion: data.functionalVersion.trim(),
      ...(data.releaseNotes ? { releaseNotes: data.releaseNotes.trim() } : {}),
      ...(data.storeUrl ? { storeUrl: data.storeUrl.trim() } : {}),
    };

    if (isEdit) {
      await onSave(base as AppVersionUpdatePayload, true);
    } else {
      await onSave(base as AppVersionCreatePayload, false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit App Version" : "New app version"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 460,
        maxHeight: 680,
        maxWidth: 760,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="platform"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={platformOptions}
                      value={field.value as string}
                      label="Platform"
                      id="app-version-platform"
                      required
                      error={!!errors.platform}
                      errorMessage={errors.platform?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="version"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="app-version-version"
                      label="Version"
                      variant="outlined"
                      required
                      placeholder="1.2.3"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.version}
                      helperText={
                        errors.version ? errors.version.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="apiVersion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="app-version-api"
                      label="API version"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.apiVersion}
                      helperText={
                        errors.apiVersion ? errors.apiVersion.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="functionalVersion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="app-version-functional"
                      label="Functional version"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.functionalVersion}
                      helperText={
                        errors.functionalVersion
                          ? errors.functionalVersion.message
                          : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="storeUrl"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="app-version-store-url"
                      label="Store URL"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.storeUrl}
                      helperText={
                        errors.storeUrl ? errors.storeUrl.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="releaseNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="app-version-release-notes"
                      label="Release notes"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.releaseNotes}
                      helperText={
                        errors.releaseNotes ? errors.releaseNotes.message : null
                      }
                      multiline
                      minRows={3}
                      maxRows={6}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default AppVersionModal;
